import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import RestauranteHistorialTable from '../components/restaurante/historial/RestauranteHistorialTable';
import RestauranteCuentaDetalleModal from '../components/restaurante/historial/RestauranteCuentaDetalleModal';
import AnularPagoModal from '../components/restaurante/mesa/AnularPagoModal';
import useRestauranteHistorial from '../hooks/restaurante/useRestauranteHistorial';
import { registrosApi } from '../api/registrosApi';
import { api } from '../lib/api';

const money = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

export default function RestauranteHistorial() {
  const {
    cuentas,
    cargando,
    error,
    filtros,
    setFiltros,
    aplicarFiltros,
    limpiarFiltros,
  } = useRestauranteHistorial();

  const [cuentaDetalle, setCuentaDetalle] = useState(null);
  const [pagoAnular, setPagoAnular] = useState(null);
  const [anulandoPago, setAnulandoPago] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [mediosPago, setMediosPago] = useState([]);

  useEffect(() => {
    let activo = true;

    const cargarOpciones = async () => {
      try {
        const [usuariosResponse, mediosResponse] =
          await Promise.all([
            registrosApi.getUsuarios(),
            registrosApi.getMediosDePago(),
          ]);

        if (!activo) return;

        setUsuarios(
          Array.isArray(usuariosResponse.data)
            ? usuariosResponse.data
            : []
        );

        setMediosPago(
          Array.isArray(mediosResponse.data)
            ? mediosResponse.data
            : []
        );
      } catch (err) {
        console.error(
          'Error cargando filtros de historial Restaurante:',
          err
        );

        if (!activo) return;

        setUsuarios([]);
        setMediosPago([]);
      }
    };

    cargarOpciones();

    return () => {
      activo = false;
    };
  }, []);

  const resumen = useMemo(() => {
    return cuentas.reduce(
      (acc, cuenta) => {
        const consumo = Number(cuenta.total || 0);
        const propina = Number(cuenta.propina_monto || 0);

        acc.cuentas += 1;
        acc.consumo += consumo;
        acc.propinas += propina;
        acc.total += consumo + propina;

        return acc;
      },
      {
        cuentas: 0,
        consumo: 0,
        propinas: 0,
        total: 0,
      }
    );
  }, [cuentas]);

  const cambiarFiltro = (campo, valor) => {
    setFiltros((actuales) => ({
      ...actuales,
      [campo]: valor,
    }));
  };

  const confirmarAnulacionPago = async ({
    pagoId,
    autorizacionPin,
    motivo,
  }) => {
    setAnulandoPago(true);
    setMensaje('');

    try {
      const response = await api.post(
        `/restaurante/pagos/${pagoId}/anular`,
        { autorizacionPin, motivo }
      );

      const resultado = response.data || {};

      setPagoAnular(null);
      setCuentaDetalle(null);
      aplicarFiltros();

      setMensaje(
        resultado.cuenta_reabierta
          ? `Pago anulado. La cuenta se reabrió${
              resultado.mesa_ocupada
                ? ' y la mesa volvió a quedar ocupada: la encontrás en Restaurante.'
                : '.'
            } Saldo pendiente: ${money(resultado.cuenta?.saldo)}.`
          : 'Pago anulado.'
      );

      return { ok: true };
    } catch (err) {
      console.error('Error anulando pago Restaurante:', err);

      return {
        ok: false,
        error:
          err?.response?.data?.error ||
          'No se pudo anular el pago.',
      };
    } finally {
      setAnulandoPago(false);
    }
  };

  return (
    <div className="min-h-screen bg-azul-oscuro text-blanco-puro">
      <Navbar />

      <main className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amarillo-dorado">
            Restaurante
          </div>

          <h1 className="mt-1 text-3xl font-black">
            Historial de cuentas
          </h1>

          <p className="mt-2 max-w-2xl text-sm text-blanco-puro/60">
            Consultá las cuentas cerradas, consumos, propinas y medios de pago.
          </p>
        </div>

        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-blanco-puro/10 bg-blanco-puro/5 p-4">
            <div className="text-xs uppercase tracking-wide text-blanco-puro/50">
              Cuentas cerradas
            </div>
            <div className="mt-1 text-2xl font-black">
              {resumen.cuentas}
            </div>
          </div>

          <div className="rounded-2xl border border-blanco-puro/10 bg-blanco-puro/5 p-4">
            <div className="text-xs uppercase tracking-wide text-blanco-puro/50">
              Consumo
            </div>
            <div className="mt-1 text-2xl font-black">
              {money(resumen.consumo)}
            </div>
          </div>

          <div className="rounded-2xl border border-blanco-puro/10 bg-blanco-puro/5 p-4">
            <div className="text-xs uppercase tracking-wide text-blanco-puro/50">
              Propinas
            </div>
            <div className="mt-1 text-2xl font-black">
              {money(resumen.propinas)}
            </div>
          </div>

          <div className="rounded-2xl border border-amarillo-dorado/30 bg-amarillo-dorado/10 p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-amarillo-dorado">
              Total
            </div>
            <div className="mt-1 text-2xl font-black text-amarillo-dorado">
              {money(resumen.total)}
            </div>
          </div>
        </div>

        <div className="mb-6 rounded-2xl border border-blanco-puro/20 bg-azul-oscuro/60 p-4 shadow-xl backdrop-blur">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <label className="block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-blanco-puro/60">
                Desde
              </span>

              <input
                type="date"
                value={filtros.desde}
                onChange={(event) =>
                  cambiarFiltro('desde', event.target.value)
                }
                className="w-full rounded-xl border border-blanco-puro/20 bg-blanco-puro/10 px-3 py-2.5 text-sm text-blanco-puro outline-none focus:border-amarillo-dorado"
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-blanco-puro/60">
                Hasta
              </span>

              <input
                type="date"
                value={filtros.hasta}
                onChange={(event) =>
                  cambiarFiltro('hasta', event.target.value)
                }
                className="w-full rounded-xl border border-blanco-puro/20 bg-blanco-puro/10 px-3 py-2.5 text-sm text-blanco-puro outline-none focus:border-amarillo-dorado"
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-blanco-puro/60">
                Cobró
              </span>

              <select
                value={filtros.usuario_id}
                onChange={(event) =>
                  cambiarFiltro(
                    'usuario_id',
                    event.target.value
                  )
                }
                className="w-full rounded-xl border border-blanco-puro/20 bg-azul-oscuro px-3 py-2.5 text-sm text-blanco-puro outline-none focus:border-amarillo-dorado"
              >
                <option value="">Todos</option>

                {usuarios.map((usuario) => (
                  <option
                    key={usuario.id}
                    value={usuario.id}
                  >
                    {usuario.nombre}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-blanco-puro/60">
                Medio de pago
              </span>

              <select
                value={filtros.medio_pago_id}
                onChange={(event) =>
                  cambiarFiltro(
                    'medio_pago_id',
                    event.target.value
                  )
                }
                className="w-full rounded-xl border border-blanco-puro/20 bg-azul-oscuro px-3 py-2.5 text-sm text-blanco-puro outline-none focus:border-amarillo-dorado"
              >
                <option value="">Todos</option>

                {mediosPago.map((medio) => (
                  <option
                    key={medio.id}
                    value={medio.id}
                  >
                    {medio.nombre}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={limpiarFiltros}
              className="rounded-xl border border-blanco-puro/20 px-5 py-2.5 text-sm font-bold text-blanco-puro transition hover:bg-blanco-puro/10"
            >
              Limpiar
            </button>

            <button
              type="button"
              onClick={aplicarFiltros}
              className="rounded-xl bg-amarillo-dorado px-5 py-2.5 text-sm font-black text-azul-oscuro transition hover:brightness-110"
            >
              Aplicar filtros
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}

        {mensaje && (
          <div className="mb-4 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-sm font-semibold text-emerald-100">
            {mensaje}
          </div>
        )}

        <RestauranteHistorialTable
          cuentas={cuentas}
          cargando={cargando}
          onDetalle={setCuentaDetalle}
        />

        <RestauranteCuentaDetalleModal
          abierto={Boolean(cuentaDetalle) && !pagoAnular}
          cuenta={cuentaDetalle}
          onClose={() => setCuentaDetalle(null)}
          onAnularPago={setPagoAnular}
        />

        <AnularPagoModal
          abierto={Boolean(pagoAnular)}
          pago={pagoAnular}
          aviso="Esta cuenta ya estaba cobrada. Al anular el pago se reabre y la mesa vuelve a quedar ocupada, siempre que siga libre."
          procesando={anulandoPago}
          onClose={() => {
            if (!anulandoPago) setPagoAnular(null);
          }}
          onConfirm={confirmarAnulacionPago}
        />
      </main>
    </div>
  );
}
